/*
  AULBERY — Transparent pricing (sections/as-transparent-pricing.liquid)
  Moved 1:1 from the section's inline <script>. The currency codes, locale
  fallback and currency display style are read from data-* attributes on the
  [data-as-tp] root. The formatting logic is unchanged. Because the script now
  loads deferred, the Intl re-format runs after parsing instead of inline —
  the Liquid-rendered figures are on screen until then.
*/
(function () {
  if (window.ASTpBooted) return;
  window.ASTpBooted = true;

  function initPricing(root) {
    if (!root || root.dataset.asxInit === '1') return;
    root.dataset.asxInit = '1';

    var currencyCode = root.dataset.currencyCode;
    var storeCurrency = root.dataset.storeCurrency;
    var locale = document.documentElement.lang || (root.dataset.langFallback || 'en');
    var currencyDisplay = root.dataset.currencyDisplay || 'narrowSymbol';
    var shopifyCurrency = window.Shopify && window.Shopify.currency ? window.Shopify.currency : null;
    var marketRate = shopifyCurrency ? parseFloat(shopifyCurrency.rate) : 1;
    if (!isFinite(marketRate) || marketRate <= 0) marketRate = 1;

    root.querySelectorAll('[data-asx-money]').forEach(function (node) {
      var amount = parseFloat(node.getAttribute('data-asx-value'));
      if (!isFinite(amount)) return;

      // Product prices supplied by Shopify are already in the customer's
      // presentment currency. Manually entered figures begin in the store's
      // base currency and use Shopify Markets' active conversion rate.
      if (node.getAttribute('data-asx-source') === 'base' && currencyCode !== storeCurrency) {
        amount *= marketRate;
      }

      var whole = node.getAttribute('data-asx-whole') === 'true' || root.dataset.asxWholeAll === '1';
      var options = {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: currencyDisplay
      };
      if (whole) {
        options.minimumFractionDigits = 0;
        options.maximumFractionDigits = 0;
      }
      node._asxAmount = amount;
      node._asxFormat = function (value) {
        try {
          return new Intl.NumberFormat(locale, options).format(value);
        } catch (error) {
          return currencyCode + ' ' + value.toFixed(whole ? 0 : 2);
        }
      };
      node.textContent = node._asxFormat(amount);
    });

    if (root.hasAttribute('data-asx-anim')) initAnimation(root);
  }

  // ---------- scroll animation ----------
  // Columns: CSS clip reveal (.asx--anim-armed → .is-inview). Prices: hidden
  // (.asx-count-wait, keeps their space) until their count starts, then pop in
  // (unless the pop is turned off: then they just appear) and count up from 0
  // to the formatted figure above, with the same easing. Sticky values: the
  // bar prices also ride up with the top of their column (CSS translate).
  // No resting "$0" is ever shown, even when the section is scrolled past fast.
  var EASE = {
    'ease-out': { css: 'cubic-bezier(0.22, 1, 0.36, 1)', fn: function (t) { return 1 - Math.pow(1 - t, 4); } },
    'ease-in-out': { css: 'cubic-bezier(0.65, 0, 0.35, 1)', fn: function (t) { return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2; } },
    linear: { css: 'linear', fn: function (t) { return t; } }
  };

  function initAnimation(root) {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var d = root.dataset;
    var duration = parseInt(d.animDuration, 10) || 1200;
    var stagger = parseInt(d.animStagger, 10) || 0;
    var ease = EASE[d.animEase] || EASE['ease-out'];
    var threshold = Math.min(Math.max((parseInt(d.animThreshold, 10) || 30) / 100, 0.01), 1);
    var replay = d.animReplay === '1';

    // [node, delay] pairs to count up
    var counters = [];
    if (d.animCountBars === '1') {
      root.querySelectorAll('.asx-bar').forEach(function (bar, i) {
        var node = bar.querySelector('[data-asx-money]');
        if (node && node._asxFormat) counters.push([node, i > 0 ? stagger : 0]);
      });
    }
    if (d.animCountCard === '1') {
      root.querySelectorAll('.asx-card .asx-now[data-asx-money], .asx-card .asx-was[data-asx-money]').forEach(function (node) {
        if (node._asxFormat) counters.push([node, 0]);
      });
    }
    if (d.animBars !== '1' && !counters.length) return;

    root.style.setProperty('--asx-anim-ease', ease.css);
    if (d.animBars === '1') root.classList.add('asx--anim-bars');
    if (d.animPop !== '0') root.classList.add('asx--anim-pop');

    // Sticky values: each bar price starts lowered by its column's height and
    // rises with the column's clip reveal (same duration, easing and delay).
    var sticky = d.animBars === '1' && d.animSticky === '1';
    function measureRise() {
      root.querySelectorAll('.asx-bar').forEach(function (bar) {
        var col = bar.querySelector('.asx-col');
        if (col) bar.style.setProperty('--asx-rise', col.offsetHeight + 'px');
      });
    }
    if (sticky) {
      root.classList.add('asx--anim-sticky');
      if (d.animCatchup === '1') root.classList.add('asx--anim-catchup');
      measureRise();
      window.addEventListener('resize', function () {
        if (!root.classList.contains('is-inview')) measureRise();
      });
    }

    function reset() {
      if (sticky) measureRise();
      counters.forEach(function (c) {
        if (c[0]._asxRaf) cancelAnimationFrame(c[0]._asxRaf);
        c[0].textContent = c[0]._asxFormat(0);
        c[0].classList.add('asx-counted');
        c[0].classList.remove('asx-count-pop');
        c[0].classList.add('asx-count-wait');
      });
    }
    function play() {
      counters.forEach(function (c) {
        var node = c[0];
        var target = node._asxAmount;
        var start = null;
        function step(now) {
          if (start === null) start = now + c[1];
          if (now < start) {
            node._asxRaf = requestAnimationFrame(step);
            return;
          }
          if (node.classList.contains('asx-count-wait')) {
            node.classList.remove('asx-count-wait');
            node.classList.add('asx-count-pop');
          }
          var t = Math.min(Math.max((now - start) / duration, 0), 1);
          node.textContent = node._asxFormat(t >= 1 ? target : target * ease.fn(t));
          if (t < 1) node._asxRaf = requestAnimationFrame(step);
        }
        node._asxRaf = requestAnimationFrame(step);
      });
    }

    reset();
    root.classList.add('asx--anim-armed');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (root.classList.contains('is-inview')) return;
          root.classList.add('is-inview');
          play();
          if (!replay) io.disconnect();
        } else if (replay && root.classList.contains('is-inview')) {
          root.classList.remove('is-inview');
          reset();
        }
      });
    }, { threshold: threshold });
    // watch the card + bars column: a tall section might never reach the
    // threshold as a whole
    io.observe(root.querySelector('.asx-right') || root);
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-as-tp]');
    for (var i = 0; i < roots.length; i++) initPricing(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();

